'use client';
import * as React from 'react';
import { useImperativeHandle } from 'react';

import { cn } from '../base/utils';

interface UseAutosizeTextAreaProps {
  textAreaRef: HTMLTextAreaElement | null;
  minHeight?: number;
  maxHeight?: number;
  triggerAutoSize: string;
}

export const useAutosizeTextArea = ({
  textAreaRef,
  triggerAutoSize,
  maxHeight = Number.MAX_SAFE_INTEGER,
  minHeight = 0,
}: UseAutosizeTextAreaProps) => {
  const [init, setInit] = React.useState(true);
  React.useEffect(() => {
    // We need to reset the height momentarily to get the correct scrollHeight for the textarea
    const offsetBorder = 2;
    if (textAreaRef) {
      if (init) {
        textAreaRef.style.minHeight = `${minHeight + offsetBorder}px`;
        if (maxHeight > minHeight) {
          textAreaRef.style.maxHeight = `${maxHeight}px`;
        }
        setInit(false);
      }
      textAreaRef.style.height = `${minHeight + offsetBorder}px`;
      const scrollHeight = textAreaRef.scrollHeight;
      // We then set the height directly, outside the render loop
      // Trying to set this with state or a ref will product an incorrect value.
      if (scrollHeight > maxHeight) {
        textAreaRef.style.height = `${maxHeight}px`;
      } else {
        textAreaRef.style.height = `${scrollHeight + offsetBorder}px`;
      }
    }
  }, [textAreaRef, triggerAutoSize]);
};

export type AutosizeTextAreaRef = {
  textArea: HTMLTextAreaElement;
  maxHeight: number;
  minHeight: number;
  focus: () => void;
};

type AutosizeTextAreaProps = {
  maxHeight?: number;
  minHeight?: number;
  onSubmit?: (e: React.FormEvent<HTMLTextAreaElement>) => void;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const AutosizeTextarea = React.forwardRef<AutosizeTextAreaRef, AutosizeTextAreaProps>(
  (
    {
      maxHeight = Number.MAX_SAFE_INTEGER,
      minHeight = 52,
      className,
      onChange,
      value,
      onKeyDown,
      onSubmit,
      onCompositionStart: propsOnCompositionStart,
      onCompositionEnd: propsOnCompositionEnd,
      ...props
    }: AutosizeTextAreaProps,
    ref: React.Ref<AutosizeTextAreaRef>,
  ) => {
    const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
    const [triggerAutoSize, setTriggerAutoSize] = React.useState('');

    // IME 组合输入（中文拼音等）：在 composition 期间用本地状态展示，避免受控 value 覆盖导致输入法中断、拼音残留
    const [isComposing, setIsComposing] = React.useState(false);
    const [compositionValue, setCompositionValue] = React.useState('');

    useAutosizeTextArea({
      textAreaRef: textAreaRef.current,
      triggerAutoSize: triggerAutoSize,
      maxHeight,
      minHeight,
    });

    useImperativeHandle(ref, () => ({
      textArea: textAreaRef.current as HTMLTextAreaElement,
      focus: () => textAreaRef.current?.focus(),
      maxHeight,
      minHeight,
    }));

    React.useEffect(() => {
      setTriggerAutoSize(value as string);
    }, [props?.defaultValue, value]);

    const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // if ArrowUp and no userInput, fill with last input
      if (e.key === 'ArrowUp' && triggerAutoSize.length <= 0 && !(e.metaKey || e.altKey || e.ctrlKey)) {
        e.preventDefault();
        return;
      }
      if (onSubmit) {
        const shouldSubmit = e.key === 'Enter' && e.keyCode !== 229 && !e.nativeEvent.isComposing && !isComposing && !e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey;
        if (shouldSubmit) {
          e.preventDefault();
          onSubmit(e);
        }
      }

      onKeyDown?.(e);
    }, [isComposing, onKeyDown, onSubmit, triggerAutoSize.length]);

    return (
      <textarea
        {...props}
        onKeyDown={handleKeyDown}
        value={typeof value !== 'undefined' ? (isComposing ? compositionValue : value ?? '') : undefined}
        ref={textAreaRef}
        className={cn(
          'flex w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        onCompositionStart={(e) => {
          setIsComposing(true);
          setCompositionValue(e.currentTarget.value);
          propsOnCompositionStart?.(e);
        }}
        onCompositionEnd={(e) => {
          setIsComposing(false);
          // 组合结束时，立即同步最终值到父组件
          const finalValue = e.currentTarget.value;
          setCompositionValue(finalValue);
          setTriggerAutoSize(finalValue);
          onChange?.(e as unknown as React.ChangeEvent<HTMLTextAreaElement>);
          propsOnCompositionEnd?.(e);
        }}
        onChange={(e) => {
          if ((e.nativeEvent as InputEvent).isComposing) {
            setCompositionValue(e.target.value);
            setTriggerAutoSize(e.target.value);
            return;
          }
          // 如果不在组合状态，正常更新
          setTriggerAutoSize(e.target.value);
          onChange?.(e);
        }}
      />
    );
  },
);
AutosizeTextarea.displayName = 'AutosizeTextarea';
